/* eslint-disable no-param-reassign */
import { ValidationError } from "sequelize"
import {
    EPSILON, HARD_SOFT,
    INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW, ACHIEVED,
} from "const"
import {
    OkrKeyResult,
    OkrKeyResultUpdate,
    OkrKeyResultUpdateCreate,
    OkrKeyResultUpdateSave,
} from "server/models"
import { GET, POST, DELETE, validate } from "client/store/utils"
import { LangType } from "client/store"
import { OkrKeyResultUpdateValidation } from "./types"


export const LoadOkrKeyResultUpdates = (keyResult: OkrKeyResult) => (
    GET(`/okrKeyResultUpdate/keyResultId/${keyResult.id}`)
    .then((res: OkrKeyResultUpdate[]) => {
        const updates: OkrKeyResultUpdate[] = []
        const updateDiff: { [index: number]: number } = {}

        res.forEach((update, index) => {
            const { diffProgress, progress } = CalculateOkrKeyResultUpdate(
                keyResult,
                update,
                updates[index - 1],
                updateDiff,
                index,
            )

            updates.push({
                ...update,
                diffProgress,
                progress,
            })
        })

        return updates
    })
)

export const LoadOkrKeyResultUpdateById = (updateId: number, keyResultId: number) => (
    GET(`/okrKeyResultUpdate/id/${updateId}/keyResultId/${keyResultId}`)
    .then((update: OkrKeyResultUpdate) => update)
)

export const SaveOkrKeyResultUpdate = (
    id: number,
    update: OkrKeyResultUpdateCreate,
    lang: LangType,
) => {
    const data: OkrKeyResultUpdateSave = {
        id,
        update,
    }
    return POST(
        "/okrKeyResultUpdate/save",
        JSON.stringify(data),
    ).then((res) => {
        const newUpdate = res as OkrKeyResultUpdate
        const error = res as ValidationError

        return {
            newUpdateId: newUpdate.id || null,
            validation: <OkrKeyResultUpdateValidation>validate(error, lang),
        }
    })
}

export const DeleteOkrKeyResultUpdate = (
    updateId: number,
    keyResultId: number,
) => (
    DELETE(`/okrKeyResultUpdate/delete/${updateId}/${keyResultId}`)
    .then((removedRows: number) => removedRows)
)

export const CalculateOkrKeyResultUpdate = (
    keyResult: OkrKeyResult,
    update: OkrKeyResultUpdate,
    prevUpdate: OkrKeyResultUpdate,
    updateDiff: { [index: number]: number },
    index: number,
) => {
    const initial = keyResult.initialNumber * EPSILON
    const target = keyResult.targetNumber * EPSILON
    const curValue = update.value * EPSILON
    const prevValue = prevUpdate ? prevUpdate.value * EPSILON : initial

    // calculate discount
    const dateUpdate = new Date(update.dateUpdate)
    const softDeadline = new Date(keyResult.deadlineSoft)
    const hardDeadline = new Date(keyResult.deadlineHard)

    const dayTime = 1000 * 60 * 60 * 24
    const softDeadlineTime = softDeadline.getTime()
    const hardDeadlineTime = hardDeadline.getTime()
    const dateUpdateTime = dateUpdate.getTime()

    // interval between soft and hard deadline, including both dates
    const deadlineDays = Math.round((hardDeadlineTime - softDeadlineTime) / dayTime) + 1
    const daysAfterSoftdeadline = Math.round((dateUpdateTime - softDeadlineTime) / dayTime)

    let diffProgress: number
    let progress: number

    if (keyResult.type === INCREASE_TO || keyResult.type === DECREASE_TO) {
        diffProgress = ((curValue - prevValue) / (target - initial)) * 100 // not rounded difference value

        if (keyResult.deadlineType === HARD_SOFT && dateUpdate > softDeadline) {
            const dayDiscount = 1 / deadlineDays // 100% divide on number of deadline days and convert to decimal
            diffProgress -= diffProgress * dayDiscount * daysAfterSoftdeadline
        }

        updateDiff[index] = diffProgress

        progress = 0
        for (let i = 0; i <= index; i++) {
            progress += updateDiff[i]
        }

        if (diffProgress < 0) diffProgress = Math.round(Math.abs(diffProgress)) * -1
        else diffProgress = Math.round(diffProgress)

        if (progress < 0) progress = Math.round(Math.abs(progress)) * -1
        else progress = Math.round(progress)
    }

    if (keyResult.type === STAY_ABOVE || keyResult.type === STAY_BELOW) {
        const critical = keyResult.useCriticalTarget && keyResult.criticalNumber !== null
            ? keyResult.criticalNumber * EPSILON
            : null
        const criticalProgress = ((curValue - critical) / (target - critical)) * 100

        if (keyResult.type === STAY_ABOVE) {
            if (curValue > target) progress = 100
            else if (critical !== null && curValue >= critical && curValue <= target) progress = criticalProgress
            else progress = 0
        }

        if (keyResult.type === STAY_BELOW) {
            if (curValue < target) progress = 100
            else if (critical !== null && curValue >= target && curValue <= critical) progress = criticalProgress
            else progress = 0
        }

        updateDiff[index] = progress
        const prevProgress = updateDiff[index - 1] || 0
        diffProgress = progress - prevProgress

        if (diffProgress < 0) diffProgress = Math.round(Math.abs(diffProgress)) * -1
        else diffProgress = Math.round(diffProgress)

        if (progress < 0) progress = Math.round(Math.abs(progress)) * -1
        else progress = Math.round(progress)
    }

    if (keyResult.type === ACHIEVED) {
        diffProgress = update.value > 0 ? 100 : -100 // 1 - achieved, 0 - not achieved

        if (keyResult.deadlineType === HARD_SOFT && dateUpdate > softDeadline) {
            const dayDiscount = 1 / deadlineDays // 100% divide on number of deadline days and convert to decimal
            diffProgress -= diffProgress * dayDiscount * daysAfterSoftdeadline
        }

        updateDiff[index] = diffProgress

        progress = 0
        for (let i = 0; i <= index; i++) {
            progress += updateDiff[i]
        }

        if (diffProgress < 0) diffProgress = Math.round(Math.abs(diffProgress)) * -1
        else diffProgress = Math.round(diffProgress)

        if (progress < 0) progress = Math.round(Math.abs(progress)) * -1
        else progress = Math.round(progress)
    }

    return { diffProgress, progress }
}
